/**
 * High-Performance In-Memory Cache & Request Deduplication for REVise Web
 * Supports Stale-While-Revalidate (SWR) pattern and TTL management
 */

interface CacheEntry<T> {
  data: T;
  timestamp: number;
  ttl: number;
}

class CacheManager {
  private cache = new Map<string, CacheEntry<unknown>>();
  private inFlight = new Map<string, Promise<unknown>>();

  /**
   * Sets a cache entry with specified TTL (in milliseconds)
   */
  set<T>(key: string, data: T, ttlMs: number = 15 * 60 * 1000): void {
    this.cache.set(key, {
      data,
      timestamp: Date.now(),
      ttl: ttlMs,
    });
  }

  /**
   * Gets cached data if available.
   * Returns { data, isFresh } or null if absent.
   */
  get<T>(key: string): { data: T; isFresh: boolean } | null {
    const entry = this.cache.get(key) as CacheEntry<T> | undefined;
    if (!entry) return null;

    const isFresh = Date.now() - entry.timestamp < entry.ttl;
    return { data: entry.data, isFresh };
  }

  /**
   * Invalidates a specific key or all keys matching a prefix
   */
  invalidate(keyOrPrefix: string): void {
    for (const key of this.cache.keys()) {
      if (key === keyOrPrefix || key.startsWith(keyOrPrefix)) {
        this.cache.delete(key);
      }
    }
  }

  /**
   * Clears all cache entries
   */
  clear(): void {
    this.cache.clear();
    this.inFlight.clear();
  }

  /**
   * Deduplicates concurrent network requests for the same URL key
   */
  async deduplicate<T>(key: string, fetcher: () => Promise<T>): Promise<T> {
    if (this.inFlight.has(key)) {
      return this.inFlight.get(key) as Promise<T>;
    }

    const promise = fetcher().finally(() => {
      this.inFlight.delete(key);
    });

    this.inFlight.set(key, promise);
    return promise;
  }
}

export const cacheManager = new CacheManager();
